import React, { useCallback } from 'react';
import { Avatar } from '@/components/atoms';
import { SearchResultSkeleton } from '@/components/molecules/skeletons';
import { SKELETON } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { Clock } from 'lucide-react';
import type { RecentSearchUser } from '@/lib/types';
import { useUserNavigation } from '@/hooks/useUserNavigation';

interface RecentSearchesProps {
  searches: RecentSearchUser[];
  isLoading: boolean;
  onSelect?: () => void;
}

export const RecentSearches: React.FC<RecentSearchesProps> = ({
  searches,
  isLoading,
  onSelect
}) => {
  const { navigateToUser, prefetchUser } = useUserNavigation();

  const handleUserClick = (username: string) => {
    navigateToUser(username);
    onSelect?.();
  };

  const handlePrefetch = useCallback((username: string) => {
    prefetchUser(username);
  }, [prefetchUser]);

  if (isLoading) {
    return (
      <div className="space-y-1 p-2">
        {Array.from({ length: SKELETON.SEARCH_RESULTS }, (_, i) => (
          <SearchResultSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <div className="p-4 text-center text-sm text-muted-foreground">
        No recent searches
      </div>
    );
  }

  return (
    <div className="space-y-1 p-2">
      <div className="flex items-center gap-2 px-2 py-1 text-xs text-muted-foreground font-medium">
        <Clock className="h-3 w-3" />
        <span>Recent</span>
      </div>
      {searches.map((user) => (
        <button
          key={user.user_id}
          onClick={() => handleUserClick(user.username)}
          onMouseEnter={() => handlePrefetch(user.username)}
          onFocus={() => handlePrefetch(user.username)}
          className={cn(
            "w-full flex items-center gap-2 p-2 rounded-lg",
            "hover:bg-accent/50 transition-colors",
            "focus:outline-none focus:bg-accent/50",
            "cursor-pointer"
          )}
        >
          <Avatar
            src={user.avatar_url}
            firstName={user.first_name}
            lastName={user.last_name}
            username={user.username}
            alt={`${user.first_name} ${user.last_name}`}
            interactive={false}
          />
          <div className="flex flex-col items-start flex-1 min-w-0">
            <p className="font-medium text-sm text-foreground truncate w-full text-left">
              {user.first_name} {user.last_name}
            </p>
            <p className="text-xs text-muted-foreground truncate w-full text-left">
              @{user.username}
            </p>
          </div>
        </button>
      ))}
    </div>
  );
};
